import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/LangContext";

function Step({ step, i, total }) {
  return (
    <motion.li
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.19, 1, 0.22, 1] }}
      className="relative grid grid-cols-12 items-start gap-6 py-10 lg:py-14 border-t border-white/10 group"
    >
      <div className="col-span-3 sm:col-span-2 flex items-start">
        <span className="chapter-num text-5xl lg:text-7xl">{step.n}</span>
      </div>
      <div className="col-span-9 sm:col-span-4 lg:col-span-3">
        <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight">{step.t}</h3>
      </div>
      <div className="col-span-12 sm:col-span-6 lg:col-span-6 text-white/60 text-base lg:text-lg leading-relaxed">
        {step.d}
      </div>
      <div className="hidden lg:flex col-span-1 justify-end pt-2">
        <span className="font-mono-ef text-[10px] tracking-[0.24em] uppercase text-white/40">
          {String(i + 1).padStart(2, "0")}/{String(total).padStart(2, "0")}
        </span>
      </div>
    </motion.li>
  );
}

export default function Journey() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="process" ref={ref} className="relative py-28 lg:py-40 overflow-hidden" data-testid="journey-section">
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute -right-40 top-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 bg-red-600/40 pointer-events-none"
      />
      <motion.div
        style={{ y }}
        aria-hidden
        className="absolute -left-40 bottom-10 w-[420px] h-[420px] rounded-full blur-3xl opacity-25 bg-cyan-800/50 pointer-events-none"
      />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 relative">
        <div className="max-w-4xl mb-14 lg:mb-20">
          <div className="text-[11px] uppercase tracking-[0.28em] text-red-400 font-mono-ef">
            — {t.journey.overline}
          </div>
          <h2 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.92] tracking-[-0.035em]">
            {t.journey.title}
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-2xl">{t.journey.subtitle}</p>
        </div>

        <ol className="border-b border-white/10">
          {t.journey.steps.map((s, i) => (
            <Step key={s.n} step={s} i={i} total={t.journey.steps.length} />
          ))}
        </ol>
      </div>
    </section>
  );
}
